// Phase 6 configuration (15 §15.2): campaigns, the points rules and the public review links.
// Saved through `saveConfig` like every other configuration record (kinds `campaign`,
// `rewardsRules` and `reviewLink` in config.ts). Kept in a file of its own so config.ts, which
// builds the registry from them, stays readable.
import { z } from 'zod';

const nonEmpty = (max = 120) => z.string().trim().min(1).max(max);
const sectorCode = z.string().regex(/^[A-Z]{2,4}$/, '2–4 capital letters, e.g. KSA');
const minor = z.number().int().nonnegative().max(1e12);

// ---- Campaigns --------------------------------------------------------------------------------

/** The code a link, an ad or a poster carries into a booking. Capital letters and digits. */
export const campaignCodeSchema = z
  .string()
  .regex(/^[A-Z0-9]{3,16}$/, '3–16 capital letters or digits, e.g. XMAS26');

export const CAMPAIGN_CHANNELS = [
  'facebook',
  'instagram',
  'tiktok',
  'google',
  'whatsapp',
  'referral',
  'agent',
  'poster',
  'event',
  'email',
  'other',
] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

/**
 * `campaigns/{cmp_…}` (15 §15.2). The booking carries the `code`; the customer's first shipment
 * copies it into `customers/{id}.acquisition`. `spend` links the campaign to marketing expenses
 * so the dashboard can show the cost of a new customer.
 */
export const campaignSchema = z
  .object({
    id: z.string().regex(/^cmp_[0-9A-HJKMNP-TV-Z]{26}$/, 'Campaign id like cmp_01J…'),
    code: campaignCodeSchema,
    name: nonEmpty(120),
    channel: z.enum(CAMPAIGN_CHANNELS),
    startsOn: z.iso.date(),
    endsOn: z.iso.date().optional(),
    /** Countries (sector codes) the campaign runs in. Empty means all. */
    sectorIds: z.array(sectorCode).max(10).default([]),
    spend: z
      .object({
        currency: z.string().regex(/^[A-Z]{3}$/),
        plannedMinor: minor.default(0),
        /** The marketing expense records (`expenses` ids, or a budget line) the spend is read from. */
        expenseRefs: z.array(z.string().max(60)).max(50).default([]),
      })
      .strict()
      .optional(),
    /** The page the campaign's links point to, e.g. `https://ecoboxgo.com/ae/?c=XMAS26`. */
    landingUrl: z.string().url().max(300),
    /** A promo code customers may type, when the campaign gives one. */
    promoCode: z
      .string()
      .regex(/^[A-Z0-9]{3,16}$/)
      .optional(),
    notes: z.string().max(500).default(''),
    active: z.boolean().default(true),
  })
  .strict()
  .refine((c) => c.endsOn === undefined || c.endsOn >= c.startsOn, {
    message: 'The campaign cannot end before it starts',
    path: ['endsOn'],
  });
export type CampaignConfig = z.infer<typeof campaignSchema>;

// ---- Points rules -----------------------------------------------------------------------------

/**
 * `rewardsRules/current` (03 §3.8, 15 §15.2). Points are earned on the amount a shipment is worth
 * (VAT-inclusive, in the country's own currency), pending until the shipment is fully delivered,
 * then confirmed. `pointValueMinor` is what one confirmed point is worth to the books
 * (06: Dr 4800 / Cr 2020 when points are confirmed).
 */
export const rewardsRulesSchema = z
  .object({
    id: z.literal('current'),
    /** Switches earning off without deleting the rules. */
    enabled: z.boolean().default(true),
    /** Points per 1.00 of the shipment's currency (so 1 = one point per riyal). */
    pointsPerMajorUnit: z.number().min(0).max(1000).default(1),
    /** Replaces `pointsPerMajorUnit` for a currency. */
    perCurrency: z.record(z.string().regex(/^[A-Z]{3}$/), z.number().min(0).max(1000)).default({}),
    /** A shipment never earns more than this many points (before the tier multiplier). */
    maxPointsPerShipment: z.number().int().positive().max(1e7).default(5000),
    /** What one confirmed point is worth in the books, in minor units of each currency. */
    pointValueMinor: z
      .record(z.string().regex(/^[A-Z]{3}$/), z.number().min(0).max(1e6))
      .default({}),
    tiers: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z]{3,20}$/),
            name: nonEmpty(40),
            /** Lifetime confirmed points needed to reach the tier. */
            minLifetime: z.number().int().nonnegative(),
            /** Earn multiplier, e.g. 1.25. */
            multiplier: z.number().min(1).max(5),
          })
          .strict(),
      )
      .min(1)
      .max(8)
      .default([{ id: 'standard', name: 'Standard', minLifetime: 0, multiplier: 1 }]),
    referral: z
      .object({
        /** Points the person who shared the code earns when the friend's first shipment is delivered. */
        referrerPoints: z.number().int().nonnegative().max(1e6).default(0),
        /** Points the friend earns on that same first delivery. */
        refereePoints: z.number().int().nonnegative().max(1e6).default(0),
      })
      .strict()
      .default({ referrerPoints: 0, refereePoints: 0 }),
    /** Confirmed points expire this many months after they were confirmed. 0 means never. */
    expiryMonths: z.number().int().min(0).max(120).default(12),
    voucherCatalog: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/),
            label: nonEmpty(80),
            pointsCost: z.number().int().positive().max(1e7),
            /** A fixed amount off, in the currency's minor units; or `percent`. */
            valueMinor: minor.optional(),
            percent: z.number().min(1).max(100).optional(),
            currency: z
              .string()
              .regex(/^[A-Z]{3}$/)
              .optional(),
            /** The least a sale must come to (VAT-inclusive, in `currency`) before the voucher can be used. */
            minSpendMinor: minor.optional(),
            validDays: z.number().int().positive().max(730).default(90),
            active: z.boolean().default(true),
          })
          .strict()
          .refine((v) => (v.valueMinor !== undefined) !== (v.percent !== undefined), {
            message: 'A voucher is either an amount or a percentage',
            path: ['valueMinor'],
          })
          .refine((v) => v.valueMinor === undefined || v.currency !== undefined, {
            message: 'An amount voucher needs its currency',
            path: ['currency'],
          }),
      )
      .max(30)
      .default([]),
  })
  .strict();
export type RewardsRulesConfig = z.infer<typeof rewardsRulesSchema>;

// ---- Review links -----------------------------------------------------------------------------

export const REVIEW_PLATFORMS = ['google', 'facebook'] as const;

/**
 * `reviewLinks/{sectorId}`: where a happy customer (4 or 5 stars) is sent to leave a public review
 * (15 §15.3), per country. A country with no link gets a plain thank-you.
 */
export const reviewLinkSchema = z
  .object({
    sectorId: sectorCode,
    links: z
      .array(
        z
          .object({
            platform: z.enum(REVIEW_PLATFORMS),
            url: z.string().url().max(400),
          })
          .strict(),
      )
      .min(1)
      .max(3),
    /** True for the seeded placeholder links. */
    isSample: z.boolean().default(false),
  })
  .strict();
export type ReviewLinkConfig = z.infer<typeof reviewLinkSchema>;
